import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  getTrainingEvalRunLogs,
  type TrainingEvalEnvProgressView,
  type TrainingEvalRunProgressView,
} from "@/api/trainingEval";
import { resolveLoadErrorMessage } from "@/components/dataset";
import { isHttpClientError } from "@/http";
import { useCursorLogs, type CursorLogsErrorDecision, type CursorLogsState } from "@/lifecycle";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { LogViewer } from "@/shared/ui/logViewer/public";
import { formatClock } from "@/utils/datetime";
import { EvalRunDetailNotice } from "./EvalRunDetailElements";
import { formatDetailNumber } from "./evalRunResultModel";
import { formatEvalEta } from "./evalRunFormat";
import { mergeEvalLogProgress, parseEvalLogProgress } from "./evalRunLogProgress";
import styles from "./EvalRunDetailView.module.css";

function resolveCursorLogsError(error: unknown): CursorLogsErrorDecision {
  return {
    message: resolveLoadErrorMessage(error),
    terminal: isHttpClientError(error) && error.kind === "notFound",
  };
}

export function EvalRunProgressSection({
  evalRunId,
  progress,
  status,
  startMs,
  fallbackNumEnvs,
}: {
  evalRunId: string;
  progress: TrainingEvalRunProgressView | null;
  status: string;
  startMs: number | null;
  fallbackNumEnvs: number | null;
}): ReactNode {
  const fetchLogs = useCallback(
    (query: { since?: string; limit: number }) => getTrainingEvalRunLogs(evalRunId, query),
    [evalRunId],
  );
  const logs = useCursorLogs(fetchLogs, {
    targetActive: true,
    resolveError: resolveCursorLogsError,
  });
  const mergedProgress = useMemo(
    () =>
      mergeEvalLogProgress(
        progress,
        parseEvalLogProgress(logs.lines.join("\n")),
        evalRunId,
        status,
      ),
    [evalRunId, logs.lines, progress, status],
  );
  const percent = mergedProgress?.percent;
  const width = Math.max(0, Math.min(100, percent ?? 0));
  const eta = formatEvalEta(mergedProgress?.eta_seconds ?? null);
  return (
    <div className={styles.progressGroup}>
      <div className={styles.progressLabels}>
        <span>{phaseLabel(mergedProgress, status)}</span>
        <span className={styles.mono}>
          {status === "running" && startMs !== null ? (
            <>
              <Elapsed startMs={startMs} /> <span className={styles.dimmed}>·</span>{" "}
            </>
          ) : null}
          {percent === null || percent === undefined ? "-" : `${formatDetailNumber(percent)}%`}
        </span>
      </div>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${width}%` }}
          role="progressbar"
          aria-valuenow={Math.round(width)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>
      {mergedProgress?.completed !== null && mergedProgress?.completed !== undefined ? (
        <span className={styles.muted}>
          완료 {mergedProgress.completed} / {mergedProgress.total ?? "-"}
          {eta === null ? "" : ` · 남은 시간 ${eta}`}
        </span>
      ) : null}
      <EnvironmentGrid progress={mergedProgress} fallbackNumEnvs={fallbackNumEnvs} />
    </div>
  );
}

function EnvironmentGrid({
  progress,
  fallbackNumEnvs,
}: {
  progress: TrainingEvalRunProgressView | null;
  fallbackNumEnvs: number | null;
}): ReactNode {
  const envs = progress?.envs ?? [];
  const numEnvs = progress?.num_envs ?? fallbackNumEnvs ?? (envs.length || null);
  if (!numEnvs) return null;
  const byIndex = new Map(envs.map((env) => [env.index, env]));
  const episodesPerEnv = progress?.episodes_per_env ?? null;
  const targets = progress?.per_env_targets ?? null;
  const done = envs.filter((env) => {
    const target = env.target ?? targets?.[env.index] ?? episodesPerEnv;
    return target !== null && target !== undefined && target > 0 && env.done >= target;
  }).length;
  const hasOutcomes = envs.some((env) => env.success + env.fp + env.fail + env.timeout > 0);
  return (
    <div className={styles.environments}>
      <div className={styles.progressLabels}>
        <span>병렬 환경</span>
        <span className={styles.mono}>
          환경 {numEnvs}개 중 {done}개 완료
          {episodesPerEnv !== null ? ` · 환경당 Episode ${episodesPerEnv}개` : ""}
        </span>
      </div>
      <ul className={styles.environmentGrid} aria-label={`병렬 환경 ${numEnvs}개`}>
        {Array.from({ length: numEnvs }, (_, index) => (
          <EnvironmentCell
            key={index}
            index={index}
            env={byIndex.get(index)}
            target={byIndex.get(index)?.target ?? targets?.[index] ?? episodesPerEnv}
          />
        ))}
      </ul>
      {hasOutcomes ? (
        <ul className={styles.legend}>
          <Legend color="var(--c6-color-status-success)" label="성공" />
          <Legend color="var(--c6-color-status-warning)" label="오탐" />
          <Legend color="var(--c6-color-status-error)" label="실패" />
          <Legend color="var(--c6-color-status-muted)" label="시간 초과" />
        </ul>
      ) : null}
    </div>
  );
}

function EnvironmentCell({
  index,
  env,
  target,
}: {
  index: number;
  env: TrainingEvalEnvProgressView | undefined;
  target: number | null | undefined;
}): ReactNode {
  const counted = env === undefined ? 0 : env.success + env.fp + env.fail + env.timeout;
  const denominator = target !== null && target !== undefined && target > 0 ? target : counted;
  const width = (value: number): string =>
    denominator > 0 ? `${Math.min(100, (value / denominator) * 100)}%` : "0%";
  const doneWidth =
    env === undefined
      ? "0%"
      : denominator > 0
        ? `${Math.min(100, (env.done / denominator) * 100)}%`
        : "0%";
  const title =
    env === undefined
      ? `env ${index} — 대기`
      : `env ${index} — Episode ${env.done}/${target ?? "?"} (성공 ${env.success} · 오탐 ${env.fp} · 실패 ${env.fail} · 시간 초과 ${env.timeout})`;
  return (
    <li className={styles.environmentCell} title={title}>
      {env !== undefined && counted > 0 ? (
        <>
          <span
            style={{ width: width(env.success), background: "var(--c6-color-status-success)" }}
          />
          <span style={{ width: width(env.fp), background: "var(--c6-color-status-warning)" }} />
          <span style={{ width: width(env.fail), background: "var(--c6-color-status-error)" }} />
          <span style={{ width: width(env.timeout), background: "var(--c6-color-status-muted)" }} />
        </>
      ) : (
        <span style={{ width: doneWidth, background: "var(--c6-color-bg-control-primary)" }} />
      )}
    </li>
  );
}

export function EvalRunFailureSection({
  evalRunId,
  status,
  observedError,
}: {
  evalRunId: string;
  status: string;
  observedError: string | null;
}): ReactNode {
  const fetchLogs = useCallback(
    (query: { since?: string; limit: number }) => getTrainingEvalRunLogs(evalRunId, query),
    [evalRunId],
  );
  const logs = useCursorLogs(fetchLogs, {
    targetActive: false,
    resolveError: resolveCursorLogsError,
  });
  return (
    <div className={styles.failureSection}>
      {observedError !== null ? (
        <EvalRunDetailNotice tone="error">{observedError}</EvalRunDetailNotice>
      ) : (
        <EmptyState size="sm">
          {status === "cancelled"
            ? "Evaluation이 취소되었습니다."
            : "Evaluation이 실패했습니다. 아래 log 를 확인해 주세요."}
        </EmptyState>
      )}
      <div className={styles.logs}>
        <div className={styles.logsHeader}>
          <span>Log</span>
          <span>{evalLogsMetaLabel(logs)}</span>
        </div>
        {/* 상자가 이미 테두리 면이다. 그래서 안내는 면을 다시 깔지 않는다. */}
        {logs.lastError !== null ? (
          <EmptyState size="sm" mode="plain" tone="error">
            Log 를 불러오지 못했습니다: {logs.lastError}
          </EmptyState>
        ) : null}
        {logs.lines.length > 0 ? (
          <LogViewer lines={logs.lines} ariaLabel="Evaluation log" />
        ) : logs.lastError !== null ? null : logs.phase === "eof" ? (
          <EmptyState size="sm" mode="plain">
            표시할 log 가 없습니다.
          </EmptyState>
        ) : (
          // 끝(eof)을 받기 전에는 "없음"으로 단정하지 않는다. 첫 응답이 비어도 다음 조회에서 줄이 올 수 있다.
          <LoadingState size="sm" mode="plain">
            Log 를 불러오고 있습니다…
          </LoadingState>
        )}
      </div>
    </div>
  );
}

// Log 상자 머리 줄의 상태. 조회 수명주기의 내부 값 대신 사용자 문구를 표시한다.
function evalLogsMetaLabel(logs: CursorLogsState): string {
  if (logs.phase === "error") {
    return "오류";
  }
  if (logs.lastError !== null) {
    return "갱신 실패 · 재시도 중";
  }
  if (logs.phase === "eof") {
    return "Log 스트림 종료";
  }
  return logs.phase === "loading" ? "불러오는 중…" : "갱신 중";
}

function Elapsed({ startMs }: { startMs: number }): ReactNode {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  return formatClock(now - startMs);
}

function phaseLabel(progress: TrainingEvalRunProgressView | null, status: string): string {
  if (["queued", "pending", "dispatched"].includes(status)) return "Container 준비 중…";
  if (status === "succeeded") return "완료";
  if (["failed", "lost"].includes(status)) return "중지됨";
  if (progress?.phase === "preparing") return "준비 중…";
  if (progress?.phase === "finishing") return "마무리 중…";
  if (
    progress?.completed !== null &&
    progress?.completed !== undefined &&
    progress.total !== null
  ) {
    return `Episode ${progress.completed} / ${progress.total}`;
  }
  return progress?.phase ?? status;
}

function Legend({ color, label }: { color: string; label: string }): ReactNode {
  return (
    <li>
      <span style={{ background: color }} />
      {label}
    </li>
  );
}
